/**
 * Pension valuation engine — JavaScript port of the original Streamlit app's
 * `utils/pension_calculator.py` and `utils/mortality_tables.py`, plus the date
 * helpers from `app.py`.
 *
 * The formulas are preserved faithfully so results match the Python tool, with
 * three deliberate, low-risk improvements that are called out inline:
 *   1. years-to-retirement is clamped at 0 (the Python version produced
 *      nonsense — shrinking salary/service — when a scenario's retirement age
 *      was below the participant's current age, e.g. "retire at 60.1" for a
 *      62-year-old).
 *   2. the spouse split ratio is a parameter (was hardcoded 50%).
 *   3. the estimated tax rate is a parameter (was three magic numbers in app.py).
 *
 * Present values now use the actuarial engine in `actuarial.js` (CPM2014
 * generational mortality, survival-weighted annuity, select-and-ultimate
 * discounting, indexation, guarantee period). The simplified life-expectancy
 * table below is retained only for reference and is no longer used in valuation.
 */
import { pensionAnnuityFactor, survivalCurve } from './actuarial.js'

// ─── Mortality / life expectancy ───────────────────────────────────────────
// Port of mortality_tables.py:get_life_expectancy. Simplified illustrative
// values — not a real actuarial table.
const MALE_STANDARD_BASE = { 55: 27.5, 60: 23.1, 65: 19.0, 70: 15.1 }
const FEMALE_STANDARD_BASE = { 55: 31.5, 60: 27.1, 65: 23.0, 70: 19.1 }
const TABLE_ADJUSTMENTS = {
  Standard: 0.0,
  Conservative: 2.5, // assumes longer life (higher pension values)
  Aggressive: -2.0, // assumes shorter life (lower pension values)
}

export function getLifeExpectancy(age, gender = 'Male', tableType = 'Standard') {
  const base = gender === 'Female' ? FEMALE_STANDARD_BASE : MALE_STANDARD_BASE
  const ages = Object.keys(base).map(Number).sort((a, b) => a - b)
  const minAge = ages[0]
  const maxAge = ages[ages.length - 1]

  // Closest key age (matches Python's min(key=abs diff)).
  let closestAge = ages[0]
  for (const a of ages) {
    if (Math.abs(a - age) < Math.abs(closestAge - age)) closestAge = a
  }
  let baseExpectancy = base[closestAge]

  if (closestAge !== age) {
    if (age < minAge) {
      // Extrapolate below the minimum.
      const slope = (base[minAge] - base[minAge + 5]) / 5
      baseExpectancy = base[minAge] + slope * (minAge - age)
    } else if (age > maxAge) {
      // Extrapolate above the maximum.
      const slope = (base[maxAge] - base[maxAge - 5]) / 5
      baseExpectancy = base[maxAge] + slope * (age - maxAge)
    } else {
      // Interpolate between the two bracketing key ages.
      let idx = ages.findIndex(x => x > age) - 1
      const lowerAge = ages[idx]
      const upperAge = ages[idx + 1]
      const lowerE = base[lowerAge]
      const upperE = base[upperAge]
      baseExpectancy =
        lowerE - ((lowerE - upperE) * (age - lowerAge)) / (upperAge - lowerAge)
    }
  }

  const adjusted = baseExpectancy + (TABLE_ADJUSTMENTS[tableType] || 0)
  return Math.max(0, adjusted)
}

// ─── Core pension value (still-working member, projected to retirement) ──────
// The annual pension at retirement is still built from salary × accrual ×
// service, but the present value is now a proper survival-weighted DEFERRED
// life annuity (CPM2014 generational mortality, select-and-ultimate discount,
// indexation, guarantee) rather than a flat life-expectancy annuity.
//
// All rates are decimals (e.g. 0.038 for 3.8%).
export function calculatePensionValue({
  currentAge,
  yearsOfService,
  currentSalary,
  salaryGrowth,
  yearsMarried,
  stopWorkNow,
  retirementAge,
  benefitAccrualRate,
  // Maximum pensionable service the plan will credit (MPP/PSPP/TPP cap at 35).
  // Projected service is capped here — without it, long-service members are
  // over-valued (the plan stops accruing past the cap). 0/Infinity = no cap.
  maxService = 35,
  // Actuarial assumptions
  valuationYear,
  nominalRate1,
  nominalRate2,
  selectYears = 20,
  indexationRate = 0,
  guaranteeYears = 10,
  gender = 'Male',
  mortalityImprovement = true,
  mortalitySector = 'composite',
}) {
  // Clamp at 0 — never project backwards if already at/past the retirement age.
  const yearsToRetirement = Math.max(0, retirementAge - currentAge)

  const cap = maxService > 0 ? maxService : Infinity
  const projectedYearsOfService = Math.min(
    stopWorkNow ? yearsOfService : yearsOfService + yearsToRetirement,
    cap,
  )

  const finalSalary = stopWorkNow
    ? currentSalary
    : currentSalary * Math.pow(1 + salaryGrowth, yearsToRetirement)

  // Pensionable earnings base = the plan's HIGHEST 5-YEAR AVERAGE, not the final
  // year. For a rising salary the final year overstates the base by ~5%; the
  // average of the five years ending at retirement is the correct figure. (For
  // a stop-work/terminate case we only have the current salary, so use it.)
  const AVG_YEARS = 5
  let earningsBase = finalSalary
  if (!stopWorkNow && salaryGrowth > 0) {
    let sum = 0
    for (let k = 0; k < AVG_YEARS; k++) sum += finalSalary * Math.pow(1 + salaryGrowth, -k)
    earningsBase = sum / AVG_YEARS
  }

  // DB formula: pensionable earnings × accrual rate × years of service.
  const annualPension = earningsBase * benefitAccrualRate * projectedYearsOfService

  // Deferred single-life annuity factor, valued at today: discounts over the
  // deferral period and weights by survival from current age to each payment.
  const pvFactor = pensionAnnuityFactor({
    currentAge,
    gender,
    valuationYear,
    nominalRate1,
    nominalRate2,
    selectYears,
    indexationRate,
    guaranteeYears,
    deferYears: yearsToRetirement,
    paymentsPerYear: 12,
    mortalityImprovement,
    mortalitySector,
  })

  const totalPresentValue = annualPension * pvFactor

  // Marital portion (proportionate service ratio).
  const maritalRatio =
    projectedYearsOfService > 0
      ? Math.min(yearsMarried / projectedYearsOfService, 1.0)
      : 0
  const maritalPresentValue = totalPresentValue * maritalRatio

  return {
    values: {
      total: totalPresentValue,
      marital: maritalPresentValue,
      maritalPercentage: maritalRatio,
    },
    details: {
      yearsToRetirement,
      projectedYearsOfService,
      finalSalary,
      annualPension,
      pvFactor,
      maritalRatio,
    },
  }
}

// ─── In-pay (already-retired) valuation ─────────────────────────────────────
// For a member already receiving their pension, there's nothing to project: we
// value the remaining stream of payments they're actually receiving as an
// IMMEDIATE life annuity from their CURRENT age. No salary/accrual/service
// formula, no deferral discounting, no early-retirement reduction.
//
// `annualPension` is the known gross annual pension in pay.
export function calculateInPayValue({
  currentAge,
  annualPension,
  valuationYear,
  nominalRate1,
  nominalRate2,
  selectYears = 20,
  indexationRate = 0,
  guaranteeYears = 10,
  gender = 'Male',
}) {
  // Immediate single-life annuity from the member's current age (deferYears 0).
  const pvFactor = pensionAnnuityFactor({
    currentAge,
    gender,
    valuationYear,
    nominalRate1,
    nominalRate2,
    selectYears,
    indexationRate,
    guaranteeYears,
    deferYears: 0,
    paymentsPerYear: 12,
  })
  const total = annualPension * pvFactor

  // Complete remaining life expectancy (for display).
  const tpx = survivalCurve(currentAge, gender, valuationYear)
  const lifeExpectancy = tpx.reduce((s, p) => s + p, 0) - 0.5

  return { total, lifeExpectancy, pvFactor }
}

// ─── Statement-driven valuation (lifetime + bridge) ─────────────────────────
// Values the pension figures straight off a member's statement: a lifetime
// pension (indexed, guaranteed) plus an optional bridge benefit paid only until
// age 65. Works for both an active member (deferred to a chosen retirement age)
// and an in-pay member (deferYears 0). Amounts are ANNUAL.
//
// `todaysDollars` true means the figures are in current-salary terms (the usual
// statement basis, which holds salary flat) and should be escalated to the
// commencement date at the wage-growth rate; false means they are already
// commencement-dated (or in pay).
export function valuePensionStream({
  lifetimeAnnual,
  bridgeAnnual = 0,
  currentAge,
  retirementAge,
  gender = 'Male',
  valuationYear,
  nominalRate1,
  nominalRate2,
  selectYears = 20,
  indexationRate = 0,
  // Post-select-period indexation (CIA split). Defaults to indexationRate; pass
  // a separate value to match a report's two-tier CPI assumption. Matters most
  // for long deferrals, where a single rate drifts (~4% on a 25-yr deferral).
  indexationRate2 = null,
  guaranteeYears = 10,
  todaysDollars = false,
  wageGrowth = 0,
  // Post-select-period pre-commencement growth (the deferred pension's CPI
  // escalation after the select boundary). Defaults to wageGrowth.
  wageGrowth2 = null,
  bridgeIndexed = false,
  mortalityImprovement = true,
  mortalitySector = 'composite',
}) {
  const deferYears = Math.max(0, retirementAge - currentAge)
  const wage = todaysDollars ? wageGrowth : 0
  const wg2 = wageGrowth2 == null ? wageGrowth : wageGrowth2
  const wage2 = todaysDollars ? wg2 : 0
  const idx2 = indexationRate2 == null ? indexationRate : indexationRate2

  const common = {
    currentAge, gender, valuationYear,
    nominalRate1, nominalRate2, selectYears,
    deferYears, paymentsPerYear: 12,
    preCommencementGrowth: wage,
    preCommencementGrowth2: wage2,
    mortalityImprovement,
    mortalitySector,
  }

  const lifeFactor = pensionAnnuityFactor({
    ...common,
    indexationRate,
    indexationRate2: idx2,
    guaranteeYears,
  })
  const lifetimeValue = lifetimeAnnual * lifeFactor

  // Bridge: a temporary benefit paid from retirement only until age 65.
  let bridgeValue = 0
  let bridgeFactor = 0
  const bridgeYears = Math.max(0, 65 - retirementAge)
  if (bridgeAnnual > 0 && bridgeYears > 0) {
    bridgeFactor = pensionAnnuityFactor({
      ...common,
      indexationRate: bridgeIndexed ? indexationRate : 0,
      indexationRate2: bridgeIndexed ? idx2 : 0,
      guaranteeYears: 0,
      maxPaymentYears: bridgeYears,
    })
    bridgeValue = bridgeAnnual * bridgeFactor
  }

  return {
    total: lifetimeValue + bridgeValue,
    lifetimeValue,
    bridgeValue,
    lifeFactor,
    bridgeFactor,
    deferYears,
  }
}

// ─── Ontario Family Law Value (FLV) ─────────────────────────────────────────
// Ontario does NOT use the B.C. setoff. The plan administrator computes a
// prescribed Family Law Value on FSRA Form FL-4B (active) / FL-4E (retired)
// under O. Reg. 287/11, valued AS AT the separation date (the Family Law
// Valuation Date, "FLVD") on a TERMINATION basis, using the CIA commuted-value
// basis (Standards of Practice §3500).
//
// This estimates that FLV. No new actuarial math: it re-anchors the existing
// `valuePensionStream` engine to the FLVD (valuationYear = separation year,
// currentAge = age at separation), then applies the CIA "50/50 rule" for the
// retirement-age assumption.
//
// On a termination basis service & salary are frozen, so the accrued deferred
// pension is fixed. The 50/50 rule's "highest-value retirement date" is found
// by valuing the member's deferred figures at each commencement age and taking
// the max; if the member is eligible for a subsidized unreduced early pension
// the FLV is the average of that value and the value at the earliest-unreduced
// age. (If not eligible, just the highest value.)
//
// `scenarios`: array of { age, lifetimeAnnual, bridgeAnnual } — the statement's
//   "deferred pension if you terminated on the separation date" figures by
//   commencement age (ANNUAL amounts).
// `earliestUnreducedAge`: the age the member could first draw an unreduced
//   pension (must match one of the scenario ages); used only when subsidized.
// `subsidizedEarly`: true if eligible for a subsidized unreduced early pension.
// `inPay`: true for a retired member (FL-4E) — value immediately at the FLVD,
//   no 50/50 rule; `scenarios[0]` carries the in-pay lifetime + bridge amounts.
export function valueOntarioFLV({
  scenarios = [],
  earliestUnreducedAge = null,
  subsidizedEarly = false,
  inPay = false,
  ageAtFLVD,
  gender = 'Male',
  separationYear,
  nominalRate1,
  nominalRate2,
  selectYears = 10,
  indexationRate = 0,
  indexationRate2 = null,
  guaranteeYears = 0,
  mortalityImprovement = true,
  mortalitySector = 'composite',
}) {
  const common = {
    currentAge: ageAtFLVD,
    gender,
    valuationYear: separationYear,
    nominalRate1, nominalRate2, selectYears,
    indexationRate, indexationRate2, guaranteeYears,
    todaysDollars: false,
    mortalityImprovement, mortalitySector,
  }

  // Retired member (FL-4E): immediate annuity at the FLVD; no 50/50 rule.
  if (inPay) {
    const sc = scenarios[0] || {}
    const v = valuePensionStream({
      ...common,
      lifetimeAnnual: sc.lifetimeAnnual || 0,
      bridgeAnnual: sc.bridgeAnnual || 0,
      retirementAge: ageAtFLVD, // deferral 0
    })
    return {
      flv: v.total,
      components: [{ age: ageAtFLVD, value: v.total, inPay: true }],
      highestValueAge: ageAtFLVD,
      earliestUnreducedAge: null,
      subsidizedEarly: false,
    }
  }

  // Active member (FL-4B): value the deferred pension at each commencement age.
  const components = scenarios
    .filter(sc => (sc.lifetimeAnnual || 0) > 0 || (sc.bridgeAnnual || 0) > 0)
    .map(sc => {
      const v = valuePensionStream({
        ...common,
        lifetimeAnnual: sc.lifetimeAnnual || 0,
        bridgeAnnual: sc.bridgeAnnual || 0,
        retirementAge: sc.age,
      })
      return { age: sc.age, value: v.total, bridgeValue: v.bridgeValue }
    })

  if (components.length === 0) {
    return { flv: 0, components: [], highestValueAge: null, earliestUnreducedAge, subsidizedEarly }
  }

  // Highest-value commencement age.
  const highest = components.reduce((a, b) => (b.value > a.value ? b : a))

  // 50/50 rule: average the highest-value age with the earliest-unreduced age
  // when the member is eligible for a subsidized unreduced early pension.
  let flv = highest.value
  let euComponent = null
  if (subsidizedEarly && earliestUnreducedAge != null) {
    euComponent = components.find(c => c.age === earliestUnreducedAge) || null
    if (euComponent) flv = 0.5 * highest.value + 0.5 * euComponent.value
  }

  return {
    flv,
    components,
    highestValueAge: highest.age,
    highestValue: highest.value,
    earliestUnreducedAge: euComponent ? euComponent.age : null,
    earliestUnreducedValue: euComponent ? euComponent.value : null,
    subsidizedEarly: !!(subsidizedEarly && euComponent),
  }
}

// ─── Date helpers (port of app.py) ──────────────────────────────────────────
// Accept ISO yyyy-mm-dd strings or Date objects; return a Date or null.
function toDate(d) {
  if (!d) return null
  if (d instanceof Date) return d
  // Parse yyyy-mm-dd as a *local* date (avoid UTC offset shifting the day).
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d)
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const parsed = new Date(d)
  return isNaN(parsed.getTime()) ? null : parsed
}

function daysInYear(year) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 366 : 365
}

function daysBetween(a, b) {
  return Math.round((b.getTime() - a.getTime()) / 86400000)
}

/** Fractional age today (or as of `asOf`). Mirrors app.py:calculate_age. */
export function calculateAge(birthDate, asOf = new Date()) {
  const birth = toDate(birthDate)
  if (!birth) return 0
  const today = asOf

  let years = today.getFullYear() - birth.getFullYear()
  if (
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
  ) {
    years -= 1
  }

  let thisYearBirthday = new Date(today.getFullYear(), birth.getMonth(), birth.getDate())
  if (thisYearBirthday > today) {
    thisYearBirthday = new Date(today.getFullYear() - 1, birth.getMonth(), birth.getDate())
  }
  const daysSince = daysBetween(thisYearBirthday, today)
  return years + daysSince / daysInYear(thisYearBirthday.getFullYear())
}

/** Fractional years between two dates. Mirrors app.py:calculate_years_between. */
export function calculateYearsBetween(startDate, endDate) {
  const start = toDate(startDate)
  const end = toDate(endDate)
  if (!start || !end) return 0
  if (start >= end) return 0

  let years = end.getFullYear() - start.getFullYear()
  if (
    end.getMonth() < start.getMonth() ||
    (end.getMonth() === start.getMonth() && end.getDate() < start.getDate())
  ) {
    years -= 1
  }

  let anniversary = new Date(end.getFullYear(), start.getMonth(), start.getDate())
  if (anniversary > end) {
    anniversary = new Date(end.getFullYear() - 1, start.getMonth(), start.getDate())
  }
  const daysSince = daysBetween(anniversary, end)
  return years + daysSince / daysInYear(anniversary.getFullYear())
}

export { toDate }
